import { useState } from 'react';
import { Table, Button } from 'react-bootstrap';
import { ClinkFromServer, CampaignsResponseDtoUnconfiguredCampaign } from '../../utils/types';
import { PagesHelper } from '../../common/PagesHelper';
import StateHelper from '../../utils/StateHelper';
import { getClinkHealthTooltip, getClinkHealthIcon } from '../../utils/clinkHealthConstants';

type SortDirection = 'asc' | 'desc';
type ClinkSortColumn = 'status' | 'clinkID' | 'sources' | 'publisher' | 'platform' | 'countries' | 'avgCost' | 'dailyCap' | 'clicks' | 'fillRate' | 'conversions' | 'conversionRate';

interface ClinkTableProps {
  clinks: ClinkFromServer[];
  matchingOnly?: boolean;
  formatNumber: (num: number | undefined) => string;
  formatPercentage: (num: number | undefined) => string;
  formatCurrency: (num: number | undefined) => string;
  handleCreateClink: (app: any) => void;
  app: any;
  onEditClink?: (clink: ClinkFromServer, app?: any) => void;
  onShowTrackingLinks?: (clink: ClinkFromServer, app?: any) => void;
  onShowHistory?: (clink: ClinkFromServer) => void;
  onShowStats?: (app: any, clink: ClinkFromServer) => void;
}

const ClinkTable = ({
  clinks,
  matchingOnly,
  formatNumber,
  formatPercentage,
  formatCurrency,
  handleCreateClink,
  app,
  onEditClink,
  onShowTrackingLinks,
  onShowHistory,
  onShowStats
}: ClinkTableProps) => {
  const [sortColumn, setSortColumn] = useState<ClinkSortColumn>('clicks');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  const trafficSources = StateHelper.getTrafficSourcesArray();

  const handleSort = (column: ClinkSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortClinks = (clinksToSort: ClinkFromServer[]) => {
    return [...clinksToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'status':
          aValue = a.configs?.[0]?.status || 'Active';
          bValue = b.configs?.[0]?.status || 'Active';
          break;
        case 'clinkID':
          aValue = a.clink_id || '';
          bValue = b.clink_id || '';
          break;
        case 'sources':
          aValue = Array.isArray(a.configs?.[0]?.campaigns) ? a.configs[0].campaigns.length : 0;
          bValue = Array.isArray(b.configs?.[0]?.campaigns) ? b.configs[0].campaigns.length : 0;
          break;
        case 'publisher':
          aValue = a.pub_name || '';
          bValue = b.pub_name || '';
          break;
        case 'platform':
          aValue = a.configs?.[0]?.platforms || '';
          bValue = b.configs?.[0]?.platforms || '';
          break;
        case 'countries':
          aValue = a.configs?.[0]?.countries || '';
          bValue = b.configs?.[0]?.countries || '';
          break;
        case 'avgCost':
          aValue = a.stats?.weekClicksCost && a.stats?.weekBuys ? a.stats.weekClicksCost / a.stats.weekBuys : 0;
          bValue = b.stats?.weekClicksCost && b.stats?.weekBuys ? b.stats.weekClicksCost / b.stats.weekBuys : 0;
          break;
        case 'dailyCap':
          aValue = a.configs?.[0]?.daily_cap || 0;
          bValue = b.configs?.[0]?.daily_cap || 0;
          break;
        case 'clicks':
          aValue = a.stats?.weekBuys || 0;
          bValue = b.stats?.weekBuys || 0;
          break;
        case 'fillRate':
          aValue = a.stats?.weekRTBFillRate || 0;
          bValue = b.stats?.weekRTBFillRate || 0;
          break;
        case 'conversions':
          aValue = a.stats?.weekInstalls || 0;
          bValue = b.stats?.weekInstalls || 0;
          break;
        case 'conversionRate':
          const aClicks = a.stats?.weekBuys || 0;
          const bClicks = b.stats?.weekBuys || 0;
          aValue = aClicks > 0 ? (a.stats?.weekInstalls || 0) / aClicks : 0;
          bValue = bClicks > 0 ? (b.stats?.weekInstalls || 0) / bClicks : 0;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      const numA = Number(aValue) || 0;
      const numB = Number(bValue) || 0;
      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });
  };

  if (!clinks || clinks.length === 0) {
    return (
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Status</th>
            <th>cLinkID</th>
            <th>Sources</th>
            <th>Publisher</th>
            <th>Platform</th>
            <th>Countries</th>
            <th className="text-right">Payout</th>
            <th className="text-right">Daily Cap</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Fill Rate</th>
            <th className="text-right">Conversions</th>
            <th className="text-right">Conv. Rate</th>
            <th className="text-right" style={{minWidth: '180px'}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={13} className="text-center">No cLinks</td>
          </tr>
        </tbody>
        </Table>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped bordered hover size="sm">
      <thead>
        <tr>
          <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('status')}>
            Status <span style={{opacity: sortColumn === 'status' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('clinkID')}>
            cLinkID <span style={{opacity: sortColumn === 'clinkID' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('sources')}>
            Sources <span style={{opacity: sortColumn === 'sources' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('publisher')}>
            Publisher <span style={{opacity: sortColumn === 'publisher' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('platform')}>
            Platform <span style={{opacity: sortColumn === 'platform' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('countries')}>
            Countries <span style={{opacity: sortColumn === 'countries' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('avgCost')}>
            Payout<span style={{opacity: sortColumn === 'avgCost' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('dailyCap')}>
            Daily Cap <span style={{opacity: sortColumn === 'dailyCap' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('clicks')}>
            Clicks <span style={{opacity: sortColumn === 'clicks' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('fillRate')}>
            Fill Rate <span style={{opacity: sortColumn === 'fillRate' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('conversions')}>
            Conversions <span style={{opacity: sortColumn === 'conversions' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => handleSort('conversionRate')}>
            Conv. Rate <span style={{opacity: sortColumn === 'conversionRate' ? 1 : 0}}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
          </th>
          <th className="text-right" style={{minWidth: '180px'}}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {// Filter and sort clinks based on matchingOnly prop, like AngularJS does
        sortClinks(matchingOnly 
          ? clinks.filter(c => c.clinkMatch === true) 
          : clinks
        ).map((clink: ClinkFromServer, idx: number) => {
          const totalClicks = (clink.stats?.weekBuys || 0);
          const conversionRate = totalClicks > 0 ? 
            (clink.stats?.weekInstalls || 0) / totalClicks : 
            0;

          return (
            <tr key={idx}>
              <td style={{whiteSpace: 'nowrap'}}>
                {clink.configs?.[0]?.status || 'Active'}
                {clink.health && (
                  <i
                    className={`fa ${getClinkHealthIcon(clink.health).icon}`}
                    style={{color: getClinkHealthIcon(clink.health).color, marginLeft: '5px'}}
                    title={getClinkHealthTooltip(clink)}
                    aria-label={getClinkHealthIcon(clink.health).ariaLabel}
                  ></i>
                )}
              </td>
              <td>
                <div>
                  <div style={{fontSize: '0.9em', color: '#666'}}>
                    {clink.clink_id}
                  </div>
                </div>
              </td>
              <td>
                {clink.configs?.[0]?.campaigns ? 
                  Array.from(new Set(clink.configs[0].campaigns.map((c: CampaignsResponseDtoUnconfiguredCampaign) => c.source)))
                    .map((sourceId, index) => {
                      return (
                        <span key={sourceId}>
                          {index > 0 && ', '}
                          <span title={sourceId}>{PagesHelper.getDemandSourceLabel(sourceId, false)}</span>
                        </span>
                      );
                    }) : 
                  '-'
                }
              </td>
              <td>
                <span title={`${clink.pub_id}:${clink.site_id}`}>
                  {PagesHelper.getTrafficSourceLabel(clink.pub_id, clink.site_id, trafficSources)}
                </span>
              </td>
              <td>
                {clink.configs?.[0]?.platforms ? (
                  <div>
                    {clink.configs[0].platforms.toLowerCase().includes('android') && (
                      <i className="fab fa-android" style={{color: '#3DDC84', fontSize: '1.2em', marginRight: '5px'}} title="Android"></i>
                    )}
                    {clink.configs[0].platforms.toLowerCase().includes('iphone') && (
                      <i className="fab fa-apple" style={{color: '#000', fontSize: '1.2em', marginRight: '5px'}} title="iPhone"></i>
                    )}
                    {clink.configs[0].platforms.toLowerCase().includes('ipad') && (
                      <i className="fab fa-apple" style={{color: '#000', fontSize: '1.2em', marginRight: '5px'}} title="iPad"></i>
                    )}
                    {clink.configs[0].platforms.toLowerCase().includes('web') && (
                      <i className="fas fa-globe" style={{color: '#4285F4', fontSize: '1.2em', marginRight: '5px'}} title="Web"></i>
                    )}
                    {/* If no recognized platform, show the text */}
                    {!clink.configs[0].platforms.toLowerCase().match(/android|iphone|ipad|web/) && (
                      <span>{clink.configs[0].platforms}</span>
                    )}
                  </div>
                ) : '-'}
              </td>
              <td>
                {clink.configs?.[0]?.countries || '-'}
              </td>
              <td className="text-right">
                {clink.configs?.[0]?.user_rev_share_p2p ? (
                  <>
                    {/* Rev share */}
                    <span>
                      {clink.publisher_rev_share ? 
                        `${(clink.publisher_rev_share * 100).toFixed(0)}` : 
                        "unknown"
                      }
                    </span>
                    <i className="fas fa-percent" style={{color: '#5cb85c', marginLeft: '3px'}}></i>
                  </>
                ) : (
                  <>
                    {/* Configured Payout */}
                    {clink.configs?.[0]?.p2p && clink.configs[0].p2p > 0 ? (
                      <span 
                        title={`cLink is configured to credit the publisher a fixed payout of (${formatCurrency(clink.configs[0].p2p)}) per credited conversion.`}
                      >
                        <span style={{color: '#000'}}>
                          {formatCurrency(clink.configs[0].p2p)}
                        </span>
                        <i className="fas fa-thumbtack" style={{marginLeft: '3px'}}></i>
                      </span>
                    ) : (
                      <span 
                        title="cLink is configured to not credit any revenue to the publisher. This is typically used for platforms where we buy traffic, rather than paying a partner per conversion or credited event."
                      >
                        <i className="fab fa-creative-commons-nc fa-lg"></i>
                      </span>
                    )}
                  </>
                )}
                <br />
                {/* Stats */}
                {clink.configs?.[0]?.user_rev_share_p2p && clink.stats?.weekInstalls && clink.stats.weekInstalls > 0 ? (
                  <span>
                    {formatCurrency((clink.stats.weekClicksCost + clink.stats.weekPublisherPayout) / clink.stats.weekInstalls)} 7d
                  </span>
                ) : (
                  clink.configs?.[0]?.user_rev_share_p2p && <span>- 7d</span>
                )}
              </td>
              <td className="text-right">{clink.configs?.[0]?.daily_cap || '-'}</td>
              <td className="text-right">{formatNumber(clink.stats?.weekBuys || 0)}</td>
              <td className="text-right">
                {formatPercentage(clink.stats?.weekRTBFillRate || 0)}
              </td>
              <td className="text-right">{formatNumber(clink.stats?.weekInstalls || 0)}</td>
              <td className="text-right">{formatPercentage(conversionRate)}</td>
              <td className="text-right">
                <div className="d-inline-flex gap-2">
                  {/* Edit Button */}
                  <Button 
                    size="sm" 
                    variant="primary" 
                    onClick={() => onEditClink?.(clink, app)} 
                    title="Edit cLink"
                  >
                    <i className="fa fa-pencil"></i>
                  </Button>
                  
                  {/* Tracking Links Button */}
                  <Button 
                    size="sm" 
                    variant="success" 
                    onClick={() => onShowTrackingLinks?.(clink, app)} 
                    title="Tracking Links"
                  >
                    <i className="fa fa-link"></i>
                  </Button>
                  
                  {/* History Button (admin only) */}
                  <Button 
                    size="sm" 
                    variant="info" 
                    onClick={() => onShowHistory?.(clink)} 
                    title="History"
                  >
                    <i className="fas fa-history"></i>
                  </Button>
                  
                  {/* Stats Button */}
                  <Button 
                    size="sm" 
                    variant="info" 
                    onClick={() => onShowStats?.(app, clink)} 
                    title="cLink Stats"
                  >
                    <i className="fa fa-bar-chart"></i>
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
    </div>
  );
};

export default ClinkTable;